import { Inject, Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { map, Observable } from 'rxjs';

import { APP_CONFIG, AppConfig } from '@src/app.config';
import { RequestService } from '@services/http/request/request.service';
import { AppRequestScope } from '@app/shared/services/core/local-scope/local-scope.types';

export type WorkspaceInvitationStatus = 'pending' | 'redeemed' | 'expired' | 'cancelled';

export interface WorkspaceInvitation {
  id: string;
  workspaceId: string;
  legalEntityId: string;
  invitedEmail: string;
  invitedEmailNormalized: string;
  tokenJti: string;
  status: WorkspaceInvitationStatus;
  role: string;
  reason?: string | null;
  expiresAt: string;
  sentAt?: string | null;
  redeemedAt?: string | null;
  redeemedByUserId?: string | null;
  redeemedByIdentityUserId?: string | null;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  cancelledAt?: string | null;
  cancelledBy?: string | null;
}

export interface CreateWorkspaceInvitationPayload {
  workspaceId: string;
  email: string;
  reason?: string | null;
  forceResendEmail?: boolean | null;
}

@Injectable({
  providedIn: 'root',
})
export class WorkspaceInvitationsApiService {
  constructor(
    @Inject(APP_CONFIG) private readonly appConfig: AppConfig,
    private readonly requestService: RequestService,
  ) {}

  private get base(): string {
    return `${this.appConfig.angularEndpoint}/workspaceInvitations`;
  }

  listPendingInvitations(
    workspaceId: string,
    regionOverride: string | null = null,
    scopeOverride: AppRequestScope | null = null,
  ): Observable<WorkspaceInvitation[]> {
    return this.requestService
      .get(this.base, { workspaceId }, true, null, regionOverride, scopeOverride)
      .pipe(map(response => response.body as WorkspaceInvitation[]));
  }

  createInvitation(
    payload: CreateWorkspaceInvitationPayload,
    regionOverride: string | null = null,
    scopeOverride: AppRequestScope | null = null,
  ): Observable<WorkspaceInvitation> {
    return this.requestService
      .post(this.base, payload, true, regionOverride, scopeOverride)
      .pipe(map(response => response.body as WorkspaceInvitation));
  }

  redeemInvitation(
    token: string,
    regionOverride: string | null = null,
    scopeOverride: AppRequestScope | null = null,
  ): Observable<WorkspaceInvitation> {
    const headers = new HttpHeaders({ 'x-org-inv-tkn': token });
    return this.requestService
      .post(`${this.base}/redeem`, {}, true, regionOverride, scopeOverride, { headers })
      .pipe(map(response => response.body as WorkspaceInvitation));
  }
}
